import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import React, { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

/**
 * "Choose your plan" layout of a Starter-eligible account (design "Purchase Portal 2 [23.9] -
 * Starters"): the Starter offer banner and a grid of vertical cards (Katalon Studio | True Manual
 * Starter | True Automation Starter). Presentational only - prices, selection and the preview
 * request stay in TrueBundlePlanSelect, which feeds these components.
 */

const ACTION_GREEN = '#0f8461';

const useStyles = makeStyles(theme => ({
  // The banner reproduces the artboard's "pp-royal2" block: saturated mint (#19d89f), dark ink
  // (#062f24), a dotted field fading in from the right, a travelling sheen and three twinkling
  // sparkles. The artboard has no close button — the small "x" in the mock IS a sparkle
  // caught mid-twinkle.
  '@keyframes starterBannerGold': {
    '0%': { backgroundPosition: '100% 0' },
    '40%, 100%': { backgroundPosition: '0% 0' },
  },
  '@keyframes starterBannerSheen': {
    '0%': { transform: 'skewX(-18deg) translateX(0)' },
    '35%, 100%': { transform: 'skewX(-18deg) translateX(700%)' },
  },
  '@keyframes starterBannerTwinkle': {
    '0%, 100%': { opacity: 0, transform: 'scale(.4) rotate(0deg)' },
    '50%': { opacity: 1, transform: 'scale(1) rotate(45deg)' },
  },
  banner: {
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    boxSizing: 'border-box',
    padding: theme.spacing(3, 4),
    marginBottom: theme.spacing(2.5),
    borderRadius: 16,
    backgroundColor: '#19d89f',
    border: '1px solid #062f2455',
    color: '#062f24',
    [theme.breakpoints.down('tablet')]: {
      padding: theme.spacing(2.5),
    },
  },
  // Static stand-in for the artboard's animated dot sweep: a dot grid fading in from the right.
  bannerDots: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: 0,
    width: '55%',
    pointerEvents: 'none',
    backgroundImage: 'radial-gradient(#062f24 1.5px, transparent 1.5px)',
    backgroundSize: '16px 16px',
    opacity: 0.16,
    maskImage: 'linear-gradient(to left, #000 55%, transparent)',
    WebkitMaskImage: 'linear-gradient(to left, #000 55%, transparent)',
  },
  bannerSheen: {
    position: 'absolute',
    top: '-50%',
    bottom: '-50%',
    left: '-30%',
    width: '22%',
    pointerEvents: 'none',
    background: 'linear-gradient(100deg, transparent, rgba(255,255,255,.22), transparent)',
    animation: '$starterBannerSheen 6s ease-in-out infinite',
    '@media (prefers-reduced-motion: reduce)': { display: 'none' },
  },
  bannerSparkle: {
    position: 'absolute',
    pointerEvents: 'none',
    color: '#062f24',
    animation: '$starterBannerTwinkle 2.8s ease-in-out infinite',
    '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 0.6 },
  },
  bannerContent: {
    position: 'relative',
    zIndex: 1,
  },
  bannerMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(0.75, 1.5),
    marginBottom: theme.spacing(1.25),
  },
  bannerTag: {
    display: 'inline-flex',
    alignItems: 'center',
    height: 24,
    padding: theme.spacing(0, 1.25),
    borderRadius: 999,
    border: '1px solid #062f24',
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: '.14em',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
  },
  bannerUntil: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    height: 24,
    fontSize: 13,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  bannerTitle: {
    margin: 0,
    fontFamily: "'Inter Tight', sans-serif",
    fontSize: 30,
    fontWeight: 600,
    letterSpacing: '-.02em',
    lineHeight: 1.15,
    color: '#062f24',
    [theme.breakpoints.down('tablet')]: {
      fontSize: 24,
    },
  },
  // The artboard's "pp2-gold": ink-coloured text with a light band sweeping through once per cycle.
  bannerHighlight: {
    background:
      'linear-gradient(100deg,#062f24 0%,#062f24 40%,#e9fff7 50%,#062f24 60%,#062f24 100%)',
    backgroundSize: '300% 100%',
    backgroundPosition: '100% 0',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    color: 'transparent',
    animation: '$starterBannerGold 5s ease-in-out infinite',
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
      backgroundPosition: '0 0',
    },
  },
  bannerSubtitle: {
    display: 'flex',
    flexWrap: 'wrap',
    columnGap: theme.spacing(1.25),
    marginTop: theme.spacing(1),
    fontSize: 14,
    lineHeight: 1.5,
    color: '#073d2e',
  },
  grid: {
    display: 'grid',
    gap: theme.spacing(1.5),
    alignItems: 'stretch',
    [theme.breakpoints.down('tablet')]: {
      gridTemplateColumns: '1fr !important',
    },
  },
  card: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    minWidth: 0,
    padding: theme.spacing(2, 2, 1.5),
    backgroundColor: '#fff',
    border: '1px solid #e1e7e4',
    outline: '2px solid transparent',
    outlineOffset: -3,
    borderRadius: 16,
    cursor: 'pointer',
    transition: 'border-color .15s ease, outline-color .15s ease',
    '&:hover': {
      borderColor: ACTION_GREEN,
    },
    '&:focus-visible': {
      outlineColor: '#19d89f',
    },
  },
  cardSelected: {
    borderColor: ACTION_GREEN,
    outline: `2px solid ${ACTION_GREEN}`,
    '&:focus-visible': {
      outlineColor: ACTION_GREEN,
    },
  },
  cardTitle: {
    fontFamily: "'Inter Tight', sans-serif",
    fontSize: 18,
    fontWeight: 600,
    letterSpacing: '-.01em',
    lineHeight: 1.2,
    color: '#0c1411',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  cardDescription: {
    marginTop: theme.spacing(-1),
    fontSize: 13,
    lineHeight: 1.4,
    color: '#5b6661',
  },
  priceBox: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    minWidth: 0,
    padding: theme.spacing(1.75),
    borderRadius: 12,
    backgroundColor: '#f3faf6',
    border: '1px solid #cfe6d8',
  },
  priceLine: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1, 1.25),
  },
  priceAmount: {
    display: 'inline-flex',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5, 1),
    fontFamily: "'Inter Tight', sans-serif",
    fontSize: 26,
    fontWeight: 600,
    letterSpacing: '-.02em',
    lineHeight: 1.1,
    color: '#0c1411',
  },
  priceUnit: {
    fontFamily: 'Inter, sans-serif',
    fontSize: 14,
    fontWeight: 400,
    letterSpacing: 0,
    color: '#5b6661',
  },
  savingBadge: {
    flex: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    height: 24,
    padding: theme.spacing(0, 1.25),
    borderRadius: 999,
    backgroundColor: ACTION_GREEN,
    color: '#fff',
    fontSize: 12,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    cursor: 'help',
  },
  savingTooltip: {
    backgroundColor: '#16141d',
    color: '#d9d8e6',
    maxWidth: 260,
    fontSize: 12,
    fontWeight: 500,
    lineHeight: 1.5,
    padding: theme.spacing(1.5, 1.75),
    border: '1px solid rgba(255, 255, 255, .14)',
    borderRadius: 12,
  },
  savingTooltipTitle: {
    fontSize: 14,
    fontWeight: 800,
    color: '#19d89f',
    marginBottom: theme.spacing(0.75),
  },
  savingTooltipList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'grid',
    gap: theme.spacing(0.5),
    '& li': {
      display: 'flex',
      gap: theme.spacing(0.875),
    },
    '& li::before': {
      content: '"•"',
      flex: 'none',
      color: '#19d89f',
    },
  },
  componentList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.25),
  },
  componentLine: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 0,
    fontSize: 14,
    lineHeight: 1.4,
  },
  componentQuantity: {
    flex: 'none',
    minWidth: 20,
    fontWeight: 600,
    color: '#0c1411',
  },
  componentPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    height: 24,
    padding: theme.spacing(0, 0.875, 0, 0.375),
    borderRadius: 999,
    border: '1px solid #e1e7e4',
    backgroundColor: '#fff',
    whiteSpace: 'nowrap',
    fontSize: 12,
    letterSpacing: '-.01em',
    color: '#16141d',
  },
  includedLink: {
    alignSelf: 'flex-start',
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    minHeight: 32,
    fontSize: 14,
    color: '#5b6661',
    textDecoration: 'none',
    '&:hover': {
      color: '#0c1411',
    },
  },
  visuallyHidden: {
    position: 'absolute',
    width: 1,
    height: 1,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
  },
}));

const HourglassIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M6.5 3h11M6.5 21h11" />
    <path d="M7.5 3v2.6c0 1.6.8 3 2.1 3.9L12 11.2l2.4-1.7c1.3-.9 2.1-2.3 2.1-3.9V3" />
    <path d="M7.5 21v-2.6c0-1.6.8-3 2.1-3.9L12 12.8l2.4 1.7c1.3.9 2.1 2.3 2.1 3.9V21" />
  </svg>
);

const ExternalLinkIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M14 5h5v5M19 5l-8 8M18 14v4a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h4" />
  </svg>
);

// Clicks inside a card control (stepper, switch, link) must not toggle the card itself.
const stopPropagation = (event: React.SyntheticEvent) => event.stopPropagation();

// The artboard's four-pointed star; mid-twinkle (rotated 45°, low opacity) it reads as a small "x".
const SPARKLE_PATH = 'M12 2l2.2 7.8L22 12l-7.8 2.2L12 22l-2.2-7.8L2 12l7.8-2.2Z';
const BANNER_SPARKLES = [
  { size: 14, right: '18%', top: '22%', delay: '0s' },
  { size: 10, right: '9%', top: '58%', delay: '.9s' },
  { size: 18, right: '28%', top: '62%', delay: '1.7s' },
];

export interface StarterOfferBannerProps {
  /** Last day of the offer, already formatted ("October 31"); no date = no "Until" line. */
  untilDate?: string;
  /** Best saving among the cards on screen; 0 drops the "up to N% off" claim. */
  savingPercent: number;
}

export const StarterOfferBanner = ({
  untilDate,
  savingPercent,
}: StarterOfferBannerProps) => {
  const classes = useStyles();

  return (
    <Box id="starter-offer-banner" role="note" className={classes.banner}>
      <span className={classes.bannerDots} aria-hidden="true" />
      <span className={classes.bannerSheen} aria-hidden="true" />
      {BANNER_SPARKLES.map(sparkle => (
        <svg
          key={sparkle.right}
          className={classes.bannerSparkle}
          aria-hidden="true"
          width={sparkle.size}
          height={sparkle.size}
          viewBox="0 0 24 24"
          style={{ right: sparkle.right, top: sparkle.top, animationDelay: sparkle.delay }}
        >
          <path d={SPARKLE_PATH} fill="currentColor" />
        </svg>
      ))}
      <Box className={`${classes.bannerMeta} ${classes.bannerContent}`}>
        <span className={classes.bannerTag}>
          <FormattedMessage id="page.plan.v2.starter.banner.tag" />
        </span>
        {untilDate && (
          <span className={classes.bannerUntil} id="starter-offer-banner-until">
            <HourglassIcon />
            <FormattedMessage id="page.plan.v2.starter.banner.until" values={{ date: untilDate }} />
          </span>
        )}
      </Box>
      <Typography
        component="h2"
        className={`${classes.bannerTitle} ${classes.bannerContent}`}
        id="starter-offer-banner-title"
      >
        {savingPercent > 0 ? (
          <FormattedMessage
            id="page.plan.v2.starter.banner.title"
            values={{
              percent: savingPercent,
              hl: (chunks: ReactNode) => <span className={classes.bannerHighlight}>{chunks}</span>,
            }}
          />
        ) : (
          <FormattedMessage id="page.plan.v2.starter.banner.title.plain" />
        )}
      </Typography>
      <Box component="p" className={`${classes.bannerSubtitle} ${classes.bannerContent}`} sx={{ mb: 0 }}>
        <span><FormattedMessage id="page.plan.v2.starter.banner.pricing" /></span>
        <span aria-hidden="true">·</span>
        <span><FormattedMessage id="page.plan.v2.starter.banner.first.purchase" /></span>
        <span aria-hidden="true">·</span>
        <span><FormattedMessage id="page.plan.v2.starter.banner.billed" /></span>
      </Box>
    </Box>
  );
};

export const StarterCardGrid = ({ columns, children }: {
  columns: number;
  children: ReactNode;
}) => {
  const classes = useStyles();

  return (
    <Box
      id="starter-card-grid"
      role="radiogroup"
      className={classes.grid}
      sx={{ gridTemplateColumns: `repeat(${Math.max(columns, 1)}, minmax(0, 1fr))` }}
    >
      {children}
    </Box>
  );
};

export interface StarterCardSavingTooltip {
  title: string;
  items: string[];
}

export interface StarterPlanCardProps {
  /** Element id prefix: `<id>-toggle` (the card), `<id>-price`, `<id>-saving`. */
  id: string;
  title: string;
  description: string;
  selected: boolean;
  onToggle: () => void;
  price: string;
  priceUnit: string;
  /** "-45%"; nothing = no badge. */
  savingLabel?: string;
  savingTooltip?: StarterCardSavingTooltip;
  onSavingTooltipOpen?: () => void;
  includedHref: string;
  onIncludedClick?: () => void;
  /** Card body inside the price box: the Studio controls, or the bundle component list. */
  children?: ReactNode;
}

export const StarterPlanCard = ({
  id,
  title,
  description,
  selected,
  onToggle,
  price,
  priceUnit,
  savingLabel,
  savingTooltip,
  onSavingTooltipOpen,
  includedHref,
  onIncludedClick,
  children,
}: StarterPlanCardProps) => {
  const classes = useStyles();
  const intl = useIntl();
  const onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    // Only the card itself: a space typed into the seat field must not toggle the card.
    if (event.target !== event.currentTarget) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggle();
    }
  };
  const badge = savingLabel ? (
    <span className={classes.savingBadge} id={`${id}-saving`}>{savingLabel}</span>
  ) : null;

  return (
    <Box
      component="article"
      id={`${id}-toggle`}
      role="radio"
      aria-checked={selected}
      aria-label={title}
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={onKeyDown}
      className={clsx(classes.card, selected && classes.cardSelected)}
      data-selected={selected}
    >
      <Typography component="div" className={classes.cardTitle}>{title}</Typography>
      <Typography className={classes.cardDescription}>{description}</Typography>
      <Box className={classes.priceBox}>
        <Box className={classes.priceLine}>
          <Typography component="span" className={classes.priceAmount} id={`${id}-price`}>
            {price}
            <span className={classes.priceUnit}>{priceUnit}</span>
          </Typography>
          {badge && savingTooltip ? (
            <Tooltip
              disableInteractive
              placement="top"
              onOpen={onSavingTooltipOpen}
              classes={{ tooltip: classes.savingTooltip }}
              title={(
                <Box>
                  <Typography className={classes.savingTooltipTitle}>
                    {savingTooltip.title}
                  </Typography>
                  <ul className={classes.savingTooltipList}>
                    {savingTooltip.items.map(item => <li key={item}>{item}</li>)}
                  </ul>
                </Box>
              )}
            >
              {badge}
            </Tooltip>
          ) : badge}
        </Box>
        {children}
      </Box>
      <Box
        component="a"
        href={includedHref}
        target="_blank"
        rel="noopener noreferrer"
        className={classes.includedLink}
        onClick={(event: React.MouseEvent) => {
          stopPropagation(event);
          onIncludedClick?.();
        }}
        onKeyDown={stopPropagation}
      >
        <FormattedMessage id="page.plan.v2.starter.card.included" />
        <ExternalLinkIcon />
        <span className={classes.visuallyHidden}>
          {intl.formatMessage({ id: 'page.plan.v2.starter.card.included.new.tab' })}
        </span>
      </Box>
    </Box>
  );
};

export interface StarterComponentLine {
  key: string;
  quantity: number;
  name: string;
  icon: ReactNode;
}

/** Bundle contents, one component per line: "3× [icon] TestOps" - never a price (Decision 2). */
export const StarterComponentList = ({ id, lines }: {
  id: string;
  lines: StarterComponentLine[];
}) => {
  const classes = useStyles();
  const intl = useIntl();

  return (
    <ul className={classes.componentList} id={id}>
      {lines.map(line => (
        <li key={line.key} className={classes.componentLine}>
          <span className={classes.componentQuantity}>
            {intl.formatMessage(
              { id: 'page.plan.v2.bundle.card.component.quantity' },
              { quantity: line.quantity },
            )}
          </span>
          {' '}
          <span className={classes.componentPill}>
            {line.icon}
            {line.name}
          </span>
        </li>
      ))}
    </ul>
  );
};

/** Wrapper for interactive controls inside a card: their clicks never toggle the card. */
export const StarterCardControls = ({ children, className }: {
  children: ReactNode;
  className?: string;
}) => (
  <Box
    className={className}
    onClick={stopPropagation}
    onKeyDown={stopPropagation}
  >
    {children}
  </Box>
);
